import { Stack, useFocusEffect, type Href } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, ScrollView } from 'react-native';

import { EventSummary } from '@/components/events/event-summary';
import { Text } from '@sixam/ui/text';
import { fetchPublishedEvents, type Event } from '@/lib/events';
import { fetchTenantBySlug, type Tenant } from '@/lib/tenant';

type TenantEventsScreenProps = Readonly<{
  eventHref: (tenant: Tenant, event: Event) => Href;
  tenantSlug: string;
}>;

export function TenantEventsScreen({ eventHref, tenantSlug }: TenantEventsScreenProps) {
  const [tenant, setTenant] = React.useState<Tenant | null>(null);
  const [events, setEvents] = React.useState<Event[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(() => {
    let active = true;
    setIsLoading(true);

    async function run() {
      try {
        const resolvedTenant = await fetchTenantBySlug(tenantSlug);
        if (!active) {
          return;
        }
        setTenant(resolvedTenant);

        if (!resolvedTenant) {
          setEvents([]);
          setError(null);
          return;
        }

        const data = await fetchPublishedEvents(resolvedTenant.id);
        if (active) {
          setEvents(data);
          setError(null);
        }
      } catch (loadError: unknown) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load events');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void run();

    return () => {
      active = false;
    };
  }, [tenantSlug]);

  useFocusEffect(load);

  return (
    <>
      <Stack.Screen options={{ headerShown: true, title: 'Events' }} />
      <Stack.Screen.BackButton displayMode="minimal">Club</Stack.Screen.BackButton>
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className="flex-1 bg-background"
        contentContainerClassName="gap-4 p-6"
      >
        <Text variant="h2">{tenant?.name ?? 'Events'}</Text>

        {isLoading ? <ActivityIndicator /> : null}

        {error ? (
          <Text className="text-destructive" selectable>
            {error}
          </Text>
        ) : null}

        {!isLoading && !tenant ? <Text variant="muted">This club is not available.</Text> : null}

        {!isLoading && tenant && events.length === 0 ? (
          <Text variant="muted">No public events right now. Check back soon.</Text>
        ) : null}

        {tenant
          ? events.map((event) => (
              <EventSummary
                key={event.id}
                event={event}
                href={eventHref(tenant, event)}
                timeZone={tenant.timezone}
              />
            ))
          : null}
      </ScrollView>
    </>
  );
}
